import * as React from 'react';
import { useState, useCallback, useMemo, useRef } from 'react';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  DragOverlay,
  type DragStartEvent,
  type DragEndEvent,
} from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { cn } from '../lib/utils';

export interface TreeNode {
  id: string;
  label: string;
  children?: TreeNode[];
  icon?: React.ReactNode;
  data?: unknown;
}

export interface NodeRenderProps {
  isSelected: boolean;
  isExpanded: boolean;
  depth: number;
  hasChildren: boolean;
  onToggle: () => void;
  onSelect?: () => void;
}

export interface TreeViewProps {
  data: TreeNode[];
  selectedId?: string;
  onSelect?: (id: string) => void;
  expandedIds?: Set<string>;
  onExpand?: (id: string) => void;
  onCollapse?: (id: string) => void;
  onMove?: (nodeId: string, newParentId: string | null, index: number) => void;
  onRename?: (nodeId: string, newLabel: string) => void;
  renderNode?: (node: TreeNode, props: NodeRenderProps) => React.ReactNode;
  draggable?: boolean;
  editable?: boolean;
  className?: string;
}

interface FlatNode {
  node: TreeNode;
  depth: number;
  parentId: string | null;
  index: number;
}

function ChevronIcon({ expanded }: { expanded: boolean }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={cn('shrink-0 transition-transform', expanded && 'rotate-90')}
    >
      <path d="m9 18 6-6-6-6" />
    </svg>
  );
}

function DragHandle() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="currentColor"
      className="shrink-0 cursor-grab text-muted-foreground/50 hover:text-muted-foreground active:cursor-grabbing"
    >
      <circle cx="9" cy="5" r="1.5" />
      <circle cx="15" cy="5" r="1.5" />
      <circle cx="9" cy="12" r="1.5" />
      <circle cx="15" cy="12" r="1.5" />
      <circle cx="9" cy="19" r="1.5" />
      <circle cx="15" cy="19" r="1.5" />
    </svg>
  );
}

function SortableTreeItem({
  flatNode,
  selectedId,
  expandedIds,
  onSelect,
  onToggle,
  onRename,
  renderNode,
  draggable,
  editable,
}: {
  flatNode: FlatNode;
  selectedId?: string;
  expandedIds?: Set<string>;
  onSelect?: (id: string) => void;
  onToggle: (id: string) => void;
  onRename?: (nodeId: string, newLabel: string) => void;
  renderNode?: TreeViewProps['renderNode'];
  draggable?: boolean;
  editable?: boolean;
}) {
  const { node, depth } = flatNode;
  const hasChildren = (node.children?.length ?? 0) > 0;
  const isExpanded = expandedIds?.has(node.id) ?? false;
  const isSelected = selectedId === node.id;
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(node.label);
  const inputRef = useRef<HTMLInputElement>(null);

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: node.id,
    disabled: !draggable,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const handleToggle = useCallback(() => onToggle(node.id), [node.id, onToggle]);
  const handleSelect = useCallback(() => onSelect?.(node.id), [node.id, onSelect]);

  const startEditing = useCallback(() => {
    if (!editable || !onRename) return;
    setEditValue(node.label);
    setIsEditing(true);
    setTimeout(() => inputRef.current?.select(), 0);
  }, [editable, onRename, node.label]);

  const commitEdit = useCallback(() => {
    const trimmed = editValue.trim();
    if (trimmed && trimmed !== node.label) {
      onRename?.(node.id, trimmed);
    }
    setIsEditing(false);
  }, [editValue, node.id, node.label, onRename]);

  const handleEditKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        commitEdit();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        setIsEditing(false);
        setEditValue(node.label);
      }
    },
    [commitEdit, node.label],
  );

  if (renderNode) {
    const nodeProps: NodeRenderProps = {
      isSelected,
      isExpanded,
      depth,
      hasChildren,
      onToggle: handleToggle,
      onSelect: handleSelect,
    };
    return (
      <div
        ref={setNodeRef}
        style={{ ...style, paddingLeft: `${depth * 20 + 4}px` }}
        className={cn(isDragging && 'opacity-50')}
        {...attributes}
        role="treeitem"
        aria-expanded={hasChildren ? isExpanded : undefined}
        aria-selected={isSelected}
        aria-level={depth + 1}
      >
        {renderNode(node, nodeProps)}
      </div>
    );
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      role="treeitem"
      aria-expanded={hasChildren ? isExpanded : undefined}
      aria-selected={isSelected}
      aria-level={depth + 1}
      className={cn('relative', isDragging && 'opacity-50')}
    >
      <div
        className={cn(
          'group flex items-center gap-1 rounded-sm px-1 py-0.5',
          isSelected ? 'bg-accent text-accent-foreground' : 'hover:bg-accent/50',
        )}
        style={{ paddingLeft: `${depth * 20 + 4}px` }}
        onClick={() => onSelect?.(node.id)}
        onDoubleClick={startEditing}
      >
        {/* Drag handle */}
        {draggable && (
          <span className="opacity-0 group-hover:opacity-100" {...listeners}>
            <DragHandle />
          </span>
        )}

        {/* Expand chevron or spacer */}
        {hasChildren ? (
          <button
            type="button"
            className="shrink-0 rounded-sm p-0.5 hover:bg-accent"
            onClick={(e) => {
              e.stopPropagation();
              handleToggle();
            }}
            aria-label={isExpanded ? 'Collapse' : 'Expand'}
          >
            <ChevronIcon expanded={isExpanded} />
          </button>
        ) : (
          <span className="w-[22px] shrink-0" />
        )}

        {/* Node icon */}
        {node.icon && <span className="shrink-0">{node.icon}</span>}

        {/* Label or edit input */}
        {isEditing ? (
          <input
            ref={inputRef}
            type="text"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={commitEdit}
            onKeyDown={handleEditKeyDown}
            className="min-w-0 flex-1 rounded bg-muted px-1 py-0 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
            onClick={(e) => e.stopPropagation()}
          />
        ) : (
          <span className="min-w-0 truncate text-sm">{node.label}</span>
        )}
      </div>
    </div>
  );
}

function flattenTree(nodes: TreeNode[], expandedIds: Set<string>, depth = 0, parentId: string | null = null): FlatNode[] {
  const result: FlatNode[] = [];
  nodes.forEach((node, index) => {
    result.push({ node, depth, parentId, index });
    if (node.children && expandedIds.has(node.id)) {
      result.push(...flattenTree(node.children, expandedIds, depth + 1, node.id));
    }
  });
  return result;
}

function findNodeById(nodes: TreeNode[], id: string): TreeNode | undefined {
  for (const node of nodes) {
    if (node.id === id) return node;
    if (node.children) {
      const found = findNodeById(node.children, id);
      if (found) return found;
    }
  }
  return undefined;
}

export function TreeView({
  data,
  selectedId,
  onSelect,
  expandedIds = new Set(),
  onExpand,
  onCollapse,
  onMove,
  onRename,
  renderNode,
  draggable = false,
  editable = false,
  className,
}: TreeViewProps) {
  const [draggedId, setDraggedId] = useState<string | null>(null);
  const treeRef = useRef<HTMLDivElement>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor),
  );

  const flatNodes = useMemo(() => flattenTree(data, expandedIds), [data, expandedIds]);
  const flatIds = useMemo(() => flatNodes.map((fn) => fn.node.id), [flatNodes]);

  const handleToggle = useCallback(
    (id: string) => {
      if (expandedIds.has(id)) {
        onCollapse?.(id);
      } else {
        onExpand?.(id);
      }
    },
    [expandedIds, onExpand, onCollapse],
  );

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setDraggedId(String(event.active.id));
  }, []);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      setDraggedId(null);
      const { active, over } = event;
      if (!over || active.id === over.id || !onMove) return;

      const activeId = String(active.id);
      const overId = String(over.id);
      const overFlat = flatNodes.find((fn) => fn.node.id === overId);
      if (!overFlat) return;

      onMove(activeId, overFlat.parentId, overFlat.index);
    },
    [flatNodes, onMove],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!selectedId) {
        if (flatIds.length > 0 && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
          e.preventDefault();
          onSelect?.(flatIds[0]);
        }
        return;
      }

      const currentIndex = flatIds.indexOf(selectedId);
      if (currentIndex === -1) return;

      switch (e.key) {
        case 'ArrowDown': {
          e.preventDefault();
          if (currentIndex < flatIds.length - 1) onSelect?.(flatIds[currentIndex + 1]);
          break;
        }
        case 'ArrowUp': {
          e.preventDefault();
          if (currentIndex > 0) onSelect?.(flatIds[currentIndex - 1]);
          break;
        }
        case 'ArrowRight': {
          e.preventDefault();
          const node = findNodeById(data, selectedId);
          if (node?.children?.length) {
            if (!expandedIds.has(selectedId)) {
              onExpand?.(selectedId);
            } else if (currentIndex < flatIds.length - 1) {
              onSelect?.(flatIds[currentIndex + 1]);
            }
          }
          break;
        }
        case 'ArrowLeft': {
          e.preventDefault();
          if (expandedIds.has(selectedId)) {
            onCollapse?.(selectedId);
          } else {
            const flat = flatNodes[currentIndex];
            if (flat?.parentId) onSelect?.(flat.parentId);
          }
          break;
        }
        case 'Enter': {
          e.preventDefault();
          onSelect?.(selectedId);
          break;
        }
        case ' ': {
          e.preventDefault();
          handleToggle(selectedId);
          break;
        }
        case 'F2': {
          if (editable && onRename) {
            e.preventDefault();
            // F2 triggers double-click behavior handled in SortableTreeItem
          }
          break;
        }
      }
    },
    [selectedId, flatIds, flatNodes, data, expandedIds, onSelect, onExpand, onCollapse, handleToggle, editable, onRename],
  );

  const draggedNode = draggedId ? findNodeById(data, draggedId) : null;

  const treeContent = (
    <div
      ref={treeRef}
      role="tree"
      tabIndex={0}
      onKeyDown={handleKeyDown}
      className={cn('select-none focus:outline-none', className)}
    >
      {flatNodes.map((flatNode) => (
        <SortableTreeItem
          key={flatNode.node.id}
          flatNode={flatNode}
          selectedId={selectedId}
          expandedIds={expandedIds}
          onSelect={onSelect}
          onToggle={handleToggle}
          onRename={onRename}
          renderNode={renderNode}
          draggable={draggable}
          editable={editable}
        />
      ))}
    </div>
  );

  if (!draggable) return treeContent;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={flatIds} strategy={verticalListSortingStrategy}>
        {treeContent}
      </SortableContext>
      <DragOverlay>
        {draggedNode && (
          <div className="rounded-sm bg-accent px-3 py-1 text-sm shadow-md">
            {draggedNode.icon && <span className="mr-1">{draggedNode.icon}</span>}
            {draggedNode.label}
          </div>
        )}
      </DragOverlay>
    </DndContext>
  );
}
